import Box from "@mui/material/Box";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import type { DashboardMonitoringData } from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";
import { useQueueMetrics } from "@/features/deployment-dashboard/analytics/hooks/use-queue-metrics";
import { DataCollectionNotice } from "@/features/deployment-dashboard/widgets/data-collection-notice";
import { DashboardMetricsPanel } from "@/features/deployment-dashboard/widgets/metrics/dashboard-metrics";
import { ActiveNotificationsPreviewPanel } from "@/features/deployment-dashboard/widgets/panels/active-notifications-preview";
import { AlertDetailsPanel } from "@/features/deployment-dashboard/notifications/alert-details-panel";
import { ZonePopulationPanel } from "@/features/deployment-dashboard/widgets/panels/zone-population";
import { QueueWaitTrendChart } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend";

interface DashboardMonitoringViewProps {
  deploymentId: string;
  data: DashboardMonitoringData;
}

/** Whole-site production dashboard summary with independent source access states. */
export function DashboardMonitoringView({ deploymentId, data }: DashboardMonitoringViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAlertId = searchParams.get("alertId");
  const queueAreaIds = useMemo(
    () => (data.areas.data ?? []).map((area) => area.id),
    [data.areas.data]
  );
  const queueChartRange = useMemo(() => {
    const snapshotTime = data.snapshotAt == null ? NaN : Date.parse(data.snapshotAt);
    const end = Number.isFinite(snapshotTime) ? snapshotTime : Date.now();
    return { min: end - 60 * 60 * 1000, max: end };
  }, [data.snapshotAt]);
  const queueMetrics = useQueueMetrics(
    deploymentId,
    queueAreaIds,
    new Date(queueChartRange.min),
    new Date(queueChartRange.max)
  );

  return (
    <Box sx={{ display: "flex", gap: 2.5, alignItems: "stretch" }}>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <DataCollectionNotice />

        <DashboardMetricsPanel
          population={data.population}
          throughput={data.throughput}
          timezone={data.timezone}
          areas={data.areas}
        />

        <Box sx={{ display: "flex", gap: 2.5, alignItems: "stretch", flexWrap: "wrap" }}>
          <Box sx={{ flex: "1 1 480px", minWidth: 0 }}>
            <ZonePopulationPanel areas={data.areas} deploymentId={deploymentId} />
          </Box>
          <Box sx={{ flex: "0 1 360px", minWidth: 0 }}>
            <ActiveNotificationsPreviewPanel
              alerts={data.alerts}

              onSelectAlert={(alertId) => setSearchParams({ alertId })}
            />
          </Box>
        </Box>

        <QueueWaitTrendChart
          xAxisRange={queueChartRange}
          queues={queueMetrics.data}
          loading={queueMetrics.isLoading}
          error={queueMetrics.error}
        />
      </Box>
      {selectedAlertId !== null && (
        <AlertDetailsPanel alertId={selectedAlertId} onClose={() => setSearchParams({})} />
      )}
    </Box>
  );
}
